import {
  Button,
  Card,
  Clickable,
  ForEach,
  Icon,
  IconButton,
  Popover,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { useLayoutEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  DateRangeCalendar,
  LocalizationProvider,
  DateRange,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateFilterCard, DateFilterValue } from './DateFilterCard';

dayjs.extend(utc);

type DateFilterProps = {
  initialValue?: DateFilterValue;
  onChange: (value?: DateFilterValue) => void;
  getCount?: (isActive: boolean, value?: DateFilterValue) => Promise<string>;
};

type CustomDatePickerProps = {
  customDateRange?: DateFilterValue;
  setCustomDateRange: (date?: DateFilterValue) => void;
  handleChange: (value?: DateFilterValue) => void;
  onPopoverClose: () => void;
};

const WEEK_IN_MILISECONDS = 7 * 24 * 60 * 60 * 1000;

const getNumberOfCards = () => {
  if (window.innerWidth < 768) return 1;
  if (window.innerWidth < 1536) return 3;
  if (window.innerWidth < 1920) return 5;
  return 7;
};

const getCurrentWeek = (currentDay = new Date()) => {
  const sunday = new Date(currentDay);
  sunday.setDate(new Date(currentDay).getDate() - currentDay.getDay());

  const saturday = new Date(currentDay);
  saturday.setDate(new Date(currentDay).getDate() + (6 - currentDay.getDay()));

  return [sunday, saturday];
};

const getWeekArray = (
  currentWeek = getCurrentWeek(),
): Array<DateFilterValue> => {
  const diffWeek = _.range(7);
  return diffWeek.map((diffValue): DateFilterValue => {
    const newSunday = new Date(currentWeek[0]);
    newSunday.setTime(
      currentWeek[0].getTime() + diffValue * WEEK_IN_MILISECONDS,
    );
    newSunday.setUTCHours(0, 0, 0, 0);

    const newSaturday = new Date(currentWeek[1]);
    newSaturday.setTime(
      currentWeek[1].getTime() + diffValue * WEEK_IN_MILISECONDS,
    );
    newSaturday.setUTCHours(23, 59, 59, 999);
    return { from: newSunday.toISOString(), to: newSaturday.toISOString() };
  });
};

function CustomDateRangePicker({
  customDateRange,
  setCustomDateRange,
  handleChange,
  onPopoverClose,
}: CustomDatePickerProps) {
  const [calendarDateRange, setCalendarDateRange] = useState<DateRange<Dayjs>>(
    customDateRange
      ? [dayjs(customDateRange.from), dayjs(customDateRange.to)]
      : [null, null],
  );

  const handleRemove = () => {
    setCustomDateRange(undefined);
    setCalendarDateRange([null, null]);
    handleChange(undefined);
    onPopoverClose();
  };

  const handleApply = () => {
    if (calendarDateRange[0] && calendarDateRange[1]) {
      const from = calendarDateRange[0].toDate();
      const to = calendarDateRange[1].toDate();
      from.setUTCHours(0, 0, 0, 0);
      to.setUTCHours(23, 59, 59, 999);

      const newCustomDate = {
        from: from.toISOString(),
        to: to.toISOString(),
      };
      setCustomDateRange(newCustomDate);
      handleChange(newCustomDate);
    }
    onPopoverClose();
  };
  return (
    <Card>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangeCalendar
          timezone="UTC"
          value={calendarDateRange}
          onChange={(newValue) => setCalendarDateRange(newValue)}
        />
      </LocalizationProvider>
      <Card.Footer>
        <Stack
          direction="row"
          content="end"
          align="center"
          gap="sm"
          className="px-5 py-2"
        >
          <Button color="secondary" onClick={handleRemove}>
            Remove
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </Stack>
      </Card.Footer>
    </Card>
  );
}

export function DateFilter(props: DateFilterProps) {
  const { initialValue, onChange, getCount } = props;
  const [value, setValue] = useState<DateFilterValue | undefined>(initialValue);
  const [startWeekArray, setStartWeekArray] = useState(getCurrentWeek());
  const [customDateRange, setCustomDateRange] = useState<
    DateFilterValue | undefined
  >(undefined);
  const [displayedWeekArraySize, setDisplayedWeekArraySize] =
    useState(getNumberOfCards());

  useLayoutEffect(() => {
    const handleResize = () => {
      setDisplayedWeekArraySize(getNumberOfCards());
      setStartWeekArray(getCurrentWeek());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  const weekArray = useMemo(
    () => getWeekArray(startWeekArray),
    [startWeekArray],
  );
  const selectedWeek = useMemo(
    () =>
      weekArray.find(
        (week) => week.from === value?.from && week.to === value?.to,
      ),
    [value, weekArray],
  );
  const weekToDisplay = useMemo(
    () => weekArray.slice(0, displayedWeekArraySize),
    [displayedWeekArraySize, weekArray],
  );

  const isAll = !value;

  const handleRightClick = () =>
    setStartWeekArray(
      startWeekArray.map(
        (date) => new Date(date.getTime() + WEEK_IN_MILISECONDS),
      ),
    );

  const handleLeftClick = () =>
    setStartWeekArray(
      startWeekArray.map(
        (date) => new Date(date.getTime() - WEEK_IN_MILISECONDS),
      ),
    );

  const handleChange = (newValue: DateFilterValue | undefined) => {
    if (newValue === value && newValue !== undefined) {
      handleChange(undefined);
    } else if (newValue !== value) {
      setValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <Stack direction="row" gap="md" align="center" className="bg-neutral-0">
      <Stack direction="row" width="none">
        <Clickable onClick={() => handleChange(undefined)}>
          <DateFilterCard
            getCount={getCount}
            value={undefined}
            isActive={isAll}
            fixedWidth
          />
        </Clickable>
        {displayedWeekArraySize !== 1 && (
          <Popover position="bottom-right">
            {({ close }) => (
              <>
                {customDateRange && customDateRange !== value ? (
                  <Clickable onClick={() => handleChange(customDateRange)}>
                    <DateFilterCard
                      getCount={getCount}
                      value={customDateRange}
                      isActive={customDateRange === value}
                      customSubtitle={
                        customDateRange && (
                          <span className="underline">Edit</span>
                        )
                      }
                      fixedWidth
                    />
                  </Clickable>
                ) : (
                  <Popover.Clickable>
                    <DateFilterCard
                      getCount={customDateRange && getCount}
                      title={customDateRange ? undefined : 'Custom'}
                      value={customDateRange}
                      isActive={customDateRange && customDateRange === value}
                      customSubtitle={
                        customDateRange && (
                          <span className="underline">Edit</span>
                        )
                      }
                      fixedWidth
                    />
                  </Popover.Clickable>
                )}
                <Popover.Content className="z-10">
                  <CustomDateRangePicker
                    customDateRange={customDateRange}
                    setCustomDateRange={setCustomDateRange}
                    handleChange={handleChange}
                    onPopoverClose={close}
                  />
                </Popover.Content>
              </>
            )}
          </Popover>
        )}
      </Stack>
      <Stack direction="row" gap="sm" align="center" className="pr-6">
        <IconButton
          label="left"
          icon={<Icon.ChevronLeft />}
          color="secondary"
          onClick={() => handleLeftClick()}
        />
        <Stack direction="row" className="overflow-hidden">
          <ForEach items={weekToDisplay}>
            {(week: DateFilterValue) => (
              <Clickable
                key={`${week.from}-${week.to}`}
                className="w-full"
                onClick={() => handleChange(week)}
              >
                <DateFilterCard
                  getCount={getCount}
                  value={week}
                  isActive={week === selectedWeek}
                />
              </Clickable>
            )}
          </ForEach>
        </Stack>
        <IconButton
          label="right"
          icon={<Icon.ChevronRight />}
          color="secondary"
          onClick={() => handleRightClick()}
        />
      </Stack>
    </Stack>
  );
}
